import React, { useState } from 'react';
import './TelephonyDirectory.css';

const DIRECTORIES = [
  {
    id: 'network_rvas',
    label: 'Telstra PSTN Network RVAs',
    description: 'Recorded Voice Announcements (RVAs) from the Telstra PSTN — intercept messages played by the network when a call cannot be completed. Includes number-not-connected, service-congested, feature-activation, and operator-diversion announcements.',
    files: [
      'misc_telstra-hold-music.mp3',
      'telstra_area-connection-unavailable.mp3',
      'telstra_area-service-congested.mp3',
      'telstra_busy-tone.mp3',
      'telstra_call-control-off.mp3',
      'telstra_call-control-on.mp3',
      'telstra_call-via-service-provider-network.mp3',
      'telstra_call-waiting-off.mp3',
      'telstra_call-waiting-on.mp3',
      'telstra_cannot-be-dialed-with-an-area-code.mp3',
      'telstra_cli-not-presented.mp3',
      'telstra_do-not-disturb-off.mp3',
      'telstra_do-not-disturb-on.mp3',
      'telstra_feature-not-provided.mp3',
      'telstra_feature-use-unsuccessful.mp3',
      'telstra_info-call-190-product.mp3',
      'telstra_messagebank_stutter.mp3',
      'telstra_network-service-congested.mp3',
      'telstra_not-compatible-with-your-equipment.mp3',
      'telstra_number-called-has-changed.mp3',
      'telstra_number-changed-transfer.mp3',
      'telstra_number-currently-unavailable.mp3',
      'telstra_number-dialled-is-busy-or-unanswered.mp3',
      'telstra_number-geographic-mobile-restriction.mp3',
      'telstra_number-not-available-from-this-service-generic.mp3',
      'telstra_number-not-available-from-this-service.mp3',
      'telstra_number-not-available-using-selected-network.mp3',
      'telstra_number-not-connected.mp3',
      'telstra_number-redirection-sample.mp3',
      'telstra_operator-assisted-calls-are-not-permitted.mp3',
      'telstra_please-use-the-alternate-number.mp3',
      'telstra_service-not-connected-generic.mp3',
      'telstra_service-type-not-provided.mp3',
      'telstra_standard-network-test-message-issue4.mp3',
      'telstra_telephone-number-has-changed.mp3',
      'telstra_telstra-retail-override-code.mp3',
      'telstra_thank-you-for-using-telstra.mp3',
      'telstra_thats.mp3',
      'telstra_this-telephone-service-is-not-connected.mp3',
      'telstra_you-have-not-been-charged.mp3',
      'telstra_your-call-could-not-be-connected.mp3',
    ].map(f => ({ name: f, path: `/telephony/network_rvas/${f}` })),
  },
  {
    id: 'asterisk',
    label: 'Asterisk Prompts',
    description: 'System voice prompts from the Asterisk open-source PBX — voicemail greetings, confirmation tones, login/logout messages, and operator transfer announcements.',
    files: [
      'auth-thankyou.wav', 'beep.wav', 'goodbye.wav', 'one-moment-please.wav',
      'transfer.wav', 'vm-deleted.wav', 'vm-goodbye.wav', 'vm-incorrect-mailbox.wav',
      'vm-incorrect.wav', 'vm-intro.wav', 'vm-login.wav', 'vm-msgsaved.wav',
      'vm-password.wav', 'vm-reachoper.wav', 'vm-review.wav', 'vm-saveoper.wav',
      'vm-sorry.wav',
    ].map(f => ({ name: f, path: `/telephony/asterisk/${f}` })),
  },
  {
    id: 'hold_music',
    label: 'Hold Music',
    description: 'On-hold music from Australian carriers and PBX manufacturers, collected from live PSTN and VoIP systems.',
    subdirs: [
      {
        id: 'hybrex',
        label: 'Hybrex',
        description: 'Default hold music shipped with the Hybrex G1E PABX system, commonly deployed in Australian small business telephone networks.',
        files: [
          { name: 'hybrex-g1e.mp3', path: '/telephony/hold_music/Hybrex/hybrex-g1e.mp3' },
        ],
      },
      {
        id: 'optus',
        label: 'Optus',
        description: 'Optus on-hold music played to callers waiting on the Optus PSTN and broadband voice network, including older high-quality recordings and the current track rotation.',
        files: [
          'Optus Hold Music High Quality.m4a',
          'Optus New Hold Music - Track 1.m4a',
          'Optus New Hold Music - Track 2.m4a',
          'Optus New Hold Music - Track 3.m4a',
          'Optus New Hold Music - Track 4.m4a',
          'Optus New Hold Music - Track 5.m4a',
        ].map(f => ({ name: f, path: `/telephony/hold_music/Optus/${encodeURIComponent(f)}` })),
      },
      {
        id: 'other',
        label: 'Other',
        description: 'Miscellaneous on-hold recordings from other Australian organisations and carriers.',
        files: [
          { name: 'RedEnergy.m4a', path: '/telephony/hold_music/Other/RedEnergy.m4a' },
        ],
      },
      {
        id: 'polycom',
        label: 'Polycom',
        description: 'High-quality hold music bundled in Polycom IP phone firmware, used as the default when no custom audio is configured.',
        files: [
          {
            name: 'Polycom Hold Music - High Quality (128kbit_AAC).m4a',
            path: '/telephony/hold_music/Polycom/Polycom%20Hold%20Music%20-%20High%20Quality%20(128kbit_AAC).m4a',
          },
        ],
      },
      {
        id: 'telstra_hold',
        label: 'Telstra',
        description: 'Telstra on-hold music spanning multiple eras of the Australian PSTN, from legacy Telecom Australia tracks through to current NBN-era compositions. Includes named tracks, numbered variations, call centre recordings, and congestion intercepts.',
        files: [
          'Telstra 2021 On-Hold Extended Variations.m4a',
          'Telstra Hold Music - Ambition.m4a',
          'Telstra Hold Music - Sun Hill.m4a',
          'Telstra Hold Music - The Road Ahead.m4a',
          'Telstra Hold Music - Track 1.m4a',
          'Telstra Hold Music - Track 2.m4a',
          'Telstra Hold Music - Track 3.m4a',
          'Telstra Hold Music - Track 4.m4a',
          'Telstra Hold Music - Track 5.m4a',
          'Telstra Hold Music - Track 6.m4a',
          'Telstra Hold Music - Track 7.m4a',
          'Telstra Hold Music - Track 8.m4a',
          'Telstra Hold Music - Track 9.m4a',
          'Telstra Hold Music - Track 10.m4a',
          'Telstra Hold Music - Track 11.m4a',
          'Telstra Hold Music - Track 12.m4a',
          'Telstra Hold Music - You Too.m4a',
          "Telstra's New Hold Music.m4a",
          'Telstra_2006_2014_HoldTracks_Various.m4a',
          'Telstra_Phillipines_Teleperformance_CallCenter_Type1.mp3',
          'ringtone_pabx.mp3',
          'telstra-ivrs-talking.wav',
          'telstra_torture.mp3',
          'telstracongestionfun.mp3',
          'telstracongestionfun2.mp3',
        ].map(f => ({ name: f, path: `/telephony/hold_music/Telstra/${encodeURIComponent(f)}` })),
        subdirs: [
          {
            id: 'telstrahold_sub',
            label: 'telstrahold',
            description: 'Additional archived Telstra hold music track.',
            files: [
              { name: 'telstra_hold_music_9.mp3', path: '/telephony/hold_music/Telstra/telstrahold/telstra_hold_music_9.mp3' },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'australian_pstn_tones',
    label: 'Australian PSTN Tones',
    description: 'Core call progress and signalling tones from the Australian PSTN, including dial tones, reorder tones, stutter tones, and call facility examples recorded directly from the Telstra network.',
    files: [
      'telstra-call-facility-example.wav',
      'telstra-call-progress-tone.wav',
      'telstra-dialtone.wav',
      'telstra-pstn-dialtone-with-reorder-tone.wav',
      'telstra-reorder-tone.wav',
      'telstra-stutter-tone.wav',
    ].map(f => ({ name: f, path: `/telephony/australian-pstn-tones/${f}` })),
  },
  {
    id: 'ringtones',
    label: 'Australian PSTN Ringtones & Call Progress Tones',
    description: 'Ringback and call progress tones from the Australian PSTN and ISDN networks, including Telstra PSTN/ISDN cadences, mobile call progress, and PABX ringback tones from CBA and others.',
    files: [
      'au_mobile_callprogress.wav',
      'auringtone_clean.wav',
      'auringtonenodelay.wav',
      'commbank_pabx_callprogress.wav',
      'isdnring.wav',
      'isdnringburst.wav',
      'isdnringnew.wav',
      'ringburst.wav',
      'ringtonetelstra.wav',
      'telstra_isdn_callprogress.wav',
      'telstra_pstn_callprogress.wav',
      'wav_rwringback.wav',
    ].map(f => ({ name: f, path: `/telephony/ringtones/${f}` })),
  },
];

function extClass(filename) {
  const ext = filename.split('.').pop().toLowerCase();
  return `ext-${ext}`;
}

function fileCount(dir) {
  const own = dir.files ? dir.files.length : 0;
  const sub = dir.subdirs
    ? dir.subdirs.reduce((sum, s) => sum + fileCount(s), 0)
    : 0;
  return own + sub;
}

function FileRow({ file, indent = 0 }) {
  const ext = file.name.split('.').pop().toLowerCase();
  return (
    <div className="td-file-row" style={{ paddingLeft: `${1 + indent * 1.5}rem` }}>
      <span className="td-file-icon">&#127925;</span>
      <a className="td-file-name" href={file.path} download>{file.name}</a>
      <span className={`td-ext-badge ${extClass(file.name)}`}>{ext}</span>
      <audio controls preload="none" src={file.path} />
    </div>
  );
}

function SubSection({ dir, indent = 1 }) {
  const [open, setOpen] = useState(true);
  const count = fileCount(dir);

  return (
    <div className="td-subsection">
      <button
        className="td-subsection-header"
        style={{ paddingLeft: `${indent * 1.5}rem` }}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className={`td-chevron ${open ? 'open' : ''}`}>&#9654;</span>
        <span className="td-subsection-title">{dir.label}</span>
        <span className="td-badge">{count} {count === 1 ? 'file' : 'files'}</span>
      </button>
      {dir.description && (
        <p className="td-description" style={{ paddingLeft: `${indent * 1.5 + 1}rem` }}>
          {dir.description}
        </p>
      )}
      {open && (
        <div>
          {dir.files && dir.files.map(f => (
            <FileRow key={f.path} file={f} indent={indent} />
          ))}
          {dir.subdirs && dir.subdirs.map(sub => (
            <SubSection key={sub.id} dir={sub} indent={indent + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function Section({ dir, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const count = fileCount(dir);

  return (
    <div className="td-section">
      <button
        className="td-section-header"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className={`td-chevron ${open ? 'open' : ''}`}>&#9654;</span>
        <span className="td-section-title">{dir.label}</span>
        <span className="td-badge">{count} {count === 1 ? 'file' : 'files'}</span>
      </button>
      {dir.description && (
        <p className="td-description">{dir.description}</p>
      )}
      {open && (
        <div className="td-section-body">
          {dir.files && dir.files.map(f => (
            <FileRow key={f.path} file={f} indent={0} />
          ))}
          {dir.subdirs && dir.subdirs.map(sub => (
            <SubSection key={sub.id} dir={sub} indent={1} />
          ))}
        </div>
      )}
    </div>
  );
}

function TelephonyDirectory() {
  const total = DIRECTORIES.reduce((sum, d) => sum + fileCount(d), 0);

  return (
    <div className="td-page">
      <div className="td-header">
        <h1 className="td-title">Telephony Audio Files</h1>
        <p className="td-subtitle">
          Australian telephony recordings &mdash; hold music, network announcements, ringtones, and Asterisk prompts.
          &nbsp;<span className="td-badge">{total} files total</span>
        </p>
      </div>
      <div className="td-list">
        {DIRECTORIES.map(dir => (
          <Section key={dir.id} dir={dir} defaultOpen={dir.id === 'network_rvas'} />
        ))}
      </div>
      <p className="td-footnote">
        Telstra is a registered trademark of the Telstra Corporation Pty Ltd. Other trademarks are registered to their respective owners. All assets comply with fair use doctrine. Please do not use these without providing credit to me.
      </p>
    </div>
  );
}

export default TelephonyDirectory;
